import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, User, Plus } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { contactosAPI } from '../api';

// Función helper para nombre completo - usa nombre_completo de la API si existe
const getNombreCompleto = (contacto) => {
  if (!contacto) return '';
  // Primero intentar usar nombre_completo que viene de la API
  if (contacto.nombre_completo) return contacto.nombre_completo;
  // Fallback: construir el nombre
  if (contacto.razon_social) return contacto.razon_social;
  let nombre = contacto.nombre || '';
  if (contacto.primer_apellido) nombre += ' ' + contacto.primer_apellido;
  if (contacto.segundo_apellido) nombre += ' ' + contacto.segundo_apellido;
  return nombre.trim();
};

const ContactoSearch = ({ 
  value,
  onChange,
  onCreateNew,
  placeholder = "Buscar contacto por nombre, email, teléfono...",
  disabled = false,
  className = "",
  showCreateButton = true,
  tipoFilter = null
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value) {
      setInputValue(getNombreCompleto(value) || value.nombre || '');
    } else {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchContactos = async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const params = { 
        search: query,
        page_size: 20
      };
      if (tipoFilter) {
        params.tipo = tipoFilter;
      }
      const response = await contactosAPI.getAll(params);
      const data = response.data?.data || response.data || [];
      setResults(data);
    } catch (error) {
      console.error('Error buscando contactos:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchContactos(newValue);
    }, 300);

    if (!newValue) {
      onChange(null);
    }
  };

  const selectContacto = (contacto) => {
    const nombreCompleto = getNombreCompleto(contacto);
    setInputValue(nombreCompleto);
    setIsOpen(false);
    setResults([]);
    onChange({ ...contacto, nombre: nombreCompleto });
  };

  const clearSelection = () => {
    setInputValue('');
    setResults([]);
    onChange(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          selectContacto(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-9 pr-8"
          data-testid="contacto-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
        )}
        {!loading && value && (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && (inputValue.length >= 2 || results.length > 0) && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {results.length > 0 ? (
            <>
              {results.map((contacto, index) => (
                <div
                  key={contacto.id}
                  onClick={() => selectContacto(contacto)}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
                    index === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`contacto-result-${contacto.id}`}
                >
                  <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {getNombreCompleto(contacto)}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {contacto.email || contacto.telefono || contacto.tipo || ''}
                    </p>
                  </div>
                </div>
              ))}
              {showCreateButton && onCreateNew && (
                <div 
                  onClick={() => onCreateNew(inputValue)}
                  className="px-3 py-2 cursor-pointer flex items-center gap-2 border-t hover:bg-green-50 text-green-700"
                  data-testid="contacto-create-new"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm">Crear nuevo contacto "{inputValue}"</span>
                </div>
              )}
            </>
          ) : !loading && inputValue.length >= 2 ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-gray-500 mb-2">No se encontraron contactos</p>
              {showCreateButton && onCreateNew && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => onCreateNew(inputValue)}
                  className="text-green-700 border-green-300 hover:bg-green-50"
                  data-testid="contacto-create-new-empty"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Crear nuevo contacto
                </Button>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ContactoSearch;
